/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { useThrottledCallback } from "@react-hookz/web";
import {
    getClientId,
    useForkRef,
    useIsScrolling,
    useRequestAnimationFrame,
    useResizeObserver,
} from "@smartsheet/lodestar-utils";
import React, { forwardRef, Ref, useCallback, useEffect, useRef, useState } from "react";
import ScrollLock from "react-scrolllock";
import { scrollbarClassNames } from "../../../constants/scrollbars";
import { Keyline, KEYLINE_HEIGHT_PROPERTY_NAME, KEYLINE_WIDTH_PROPERTY_NAME } from "../styled/Keyline";
import { ScrollAreaScrollStyled } from "../styled/ScrollAreaScrollStyled";
import { ScrollAreaStyled } from "../styled/ScrollAreaStyled";
import { ScrollAreaGutter } from "../types";
import { getScrollInfo } from "../utils/scrollUtils";

export interface ScrollAreaProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * Ref for the underlying scollable area element.
     */
    childScrollingAreaRef?: Ref<HTMLDivElement>;

    /**
     * A `clientId` prop is provided for specified elements, which is a unique string
     * that appears as a data attribute `data-client-id` in the rendered code, serving as
     * a hook for automated tests
     */
    clientId?: string;

    /**
     * The direction the scrollarea should scroll. This has implications for the keylines that can
     * be shown.
     *
     * Defaults to "vertical". If "horizontal", the scroll area will scroll horizontally instead of
     * vertically.
     */
    direction?: "horizontal" | "vertical";

    /**
     * gutter is the amount of padding/margin that should be applied to the StyledScrollAreaScroll/Keyline component.
     * Default is Gutter.Legacy
     */
    gutter?: ScrollAreaGutter;

    /**
     * If true, the scroll area will render a scrolling indication when the user scrolls. This is
     * theme-dependent but if used by the theme, will render something different when the user is
     * scrolling the ScrollArea.
     *
     * Defaults to true. Note this does nothing in the Lodestar or legacy themes.
     */
    includeScrollingIndication?: boolean;

    /**
     * set to true to lock the page from scrolling. Useful for modal contexts.
     */
    lockPageScroll?: boolean;

    /**
     * Apply a specific scrollbar style.
     */
    scrollbarStyle?: keyof typeof scrollbarClassNames;

    /**
     * If true, the keylines will span the entire length of the container regardless of inner padding
     */
    showFullWidthKeylines?: boolean;

    /**
     * If true, the scroll area will render a keyline at the top and bottom of the scroll area. The
     * keyline indicates there is more content available to scroll to.
     *
     * Defaults to "end" for back-compat.
     */
    showKeyLines?: "none" | "start" | "end" | "both";
}

/**
 * ScrollArea is a component to handle a vertically scrolling content area. It is responsible for:
 * - Rendering the content area
 * - Providing a scrolling context for the content area
 * - Optionally locking browser scroll (useful for when being used inside a modal, so the page doesn't scroll behind it)
 * - Rendering an affordance that there's more content below the fold
 */
export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
    (
        {
            childScrollingAreaRef,
            children,
            clientId,
            gutter = "legacy",
            lockPageScroll = false,
            scrollbarStyle,
            className,
            showKeyLines = "end",
            showFullWidthKeylines = false,
            includeScrollingIndication = true,
            direction = "vertical",
            ...rest
        },
        providedRef
    ) => {
        const [showStartKeyline, setShowStartKeyline] = useState(false);
        const [showEndKeyline, setShowEndKeyline] = useState(false);
        const { isScrolling, ref: isScrollingRef } = useIsScrolling();
        const scrollContainerRef = useRef<HTMLDivElement | null>(null);
        const scrollAreaRootRef = useRef<HTMLDivElement | null>(null);
        const combinedRootRef = useForkRef(scrollAreaRootRef, providedRef);

        const renderStartKeyline = (showKeyLines === "start" || showKeyLines === "both") && showStartKeyline;
        const renderEndKeyline = (showKeyLines === "end" || showKeyLines === "both") && showEndKeyline;

        const { raf } = useRequestAnimationFrame();
        const determineKeylines = useCallback(() => {
            raf(() => {
                const scrollContainer = scrollContainerRef.current;
                const scrollAreaRoot = scrollAreaRootRef.current;
                if (!scrollContainer || !scrollAreaRoot) {
                    return;
                }

                const { scrollTop, scrollLeft, contentWidth, contentHeight, viewportHeight, viewportWidth } =
                    getScrollInfo(scrollContainer);

                if (direction === "vertical") {
                    setShowStartKeyline(scrollTop > 0);
                    // We're going to remove 1px from the scrollable distance to account for any weird
                    // sub-pixel math with zooming.
                    const scrollableDistance = contentHeight - viewportHeight;
                    setShowEndKeyline(scrollTop < scrollableDistance - 1);

                    // Set the keyline's padding to the current width of the scrollContainer, minus it's left/right padding.
                    const scrollContainerStyle = window.getComputedStyle(scrollContainer);
                    const scrollContainerWidth =
                        scrollContainer.clientWidth -
                        parseFloat(scrollContainerStyle.paddingLeft) -
                        parseFloat(scrollContainerStyle.paddingRight);

                    scrollAreaRoot.style.setProperty(
                        KEYLINE_WIDTH_PROPERTY_NAME,
                        showFullWidthKeylines ? "100%" : `${scrollContainerWidth}px`
                    );
                } else {
                    setShowStartKeyline(scrollLeft > 0);
                    // We're going to remove 1px from the scrollable distance to account for any weird
                    // sub-pixel math with zooming.
                    const scrollableDistance = contentWidth - viewportWidth;
                    setShowEndKeyline(scrollLeft < scrollableDistance - 1);

                    // Set the keyline's padding to the current width of the scrollContainer, minus it's left/right padding.
                    const scrollContainerStyle = window.getComputedStyle(scrollContainer);
                    const scrollContainerHeight =
                        scrollContainer.clientHeight -
                        parseFloat(scrollContainerStyle.paddingTop) -
                        parseFloat(scrollContainerStyle.paddingBottom);

                    scrollAreaRoot.style.setProperty(KEYLINE_HEIGHT_PROPERTY_NAME, `${scrollContainerHeight}px`);
                }
            });
        }, [raf, direction, showFullWidthKeylines]);

        // Attach handlers to the scroll container's current element to show/hide keylines.
        useEffect(() => {
            const containerElement = scrollContainerRef.current;
            if (containerElement && showKeyLines !== "none") {
                // Run this initially to determine if we need to show the keyline.
                determineKeylines();

                containerElement.addEventListener("scroll", determineKeylines, {
                    passive: true,
                });
                return () => {
                    containerElement.removeEventListener("scroll", determineKeylines);
                };
            }
        }, [determineKeylines, showKeyLines]);

        // We also need to evaluate the keyline's visibility if the container resizes.
        const { ref: resizeObserverRef } = useResizeObserver(
            useCallback(() => {
                const containerElement = scrollContainerRef.current;
                if (containerElement) {
                    determineKeylines();
                }
            }, [determineKeylines]),
            {
                enabled: showKeyLines !== "none",
            }
        );

        const combinedRef = useForkRef(
            scrollContainerRef,
            isScrollingRef,
            resizeObserverRef,
            childScrollingAreaRef ?? null
        );

        const timerId = useRef<NodeJS.Timeout | null>(null);

        // Fix for https://git.lab.smartsheet.com/design-systems-team/lodestar-core/-/issues/589.
        // We need to quickly toggle the ScrollLock component off/on when the window resizes. This
        // causes the ScrollLock component to re-calculate the window height and update the CSS
        // properties it applies to the body when scrolling is locked.
        const [localScrollLockIsActive, setLocalScrollIsActive] = useState(lockPageScroll);
        const throttledHandleResize = useThrottledCallback(
            () => {
                setLocalScrollIsActive(false);
                if (timerId.current) {
                    clearTimeout(timerId.current);
                }
                timerId.current = setTimeout(() => {
                    setLocalScrollIsActive(true);
                });
            },
            [],
            30
        );
        useEffect(() => {
            window.addEventListener("resize", throttledHandleResize);
            return () => {
                window.removeEventListener("resize", throttledHandleResize);
                if (timerId.current) {
                    clearTimeout(timerId.current);
                }
            };
        }, [throttledHandleResize]);

        return (
            <ScrollAreaStyled className={className} {...rest} ref={combinedRootRef}>
                <ScrollAreaScrollStyled
                    direction={direction}
                    data-client-id={clientId}
                    gutter={gutter}
                    ref={combinedRef}
                    isScrollable={showStartKeyline || showEndKeyline}
                    className={scrollbarStyle && scrollbarClassNames[scrollbarStyle]}
                >
                    {children}
                    {lockPageScroll && <ScrollLock isActive={localScrollLockIsActive} />}
                </ScrollAreaScrollStyled>

                <Keyline
                    direction={direction}
                    data-client-id={getClientId(clientId, "scrollarea-keyline-top")}
                    isVisible={showStartKeyline && renderStartKeyline}
                    gutter={gutter}
                    isScrolling={isScrolling}
                    includeScrollingIndication={includeScrollingIndication}
                    alignTop
                />

                <Keyline
                    direction={direction}
                    data-client-id={getClientId(clientId, "lodestar-keyline")}
                    isVisible={showEndKeyline && renderEndKeyline}
                    gutter={gutter}
                    isScrolling={isScrolling}
                    includeScrollingIndication={includeScrollingIndication}
                />
            </ScrollAreaStyled>
        );
    }
);

ScrollArea.displayName = "ScrollArea";
